import { Box, Flex, Skeleton, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'

const MotionBox = motion(Box)

/* ── Shared chart tooltip ── */
interface ChartTooltipProps {
  active?: boolean
  payload?: any[]
  labelFormatter?: (label: string) => string
  valueFormatter: (value: any, payload: any) => string
}

export function ChartTooltip({ active, payload, labelFormatter, valueFormatter }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  const entry = payload[0]
  const label = labelFormatter ? labelFormatter(entry.name) : entry.name

  return (
    <Box bg="white" borderRadius="10px" border="1px solid #ECEFF3" boxShadow="0px 4px 12px rgba(0,0,0,0.08)" px={3} py={2}>
      <Text fontSize="12px" color="solid.900" fontWeight={600} mb="2px">{label}</Text>
      <Text fontSize="12px" color="solid.600">
        <Box as="span" fontWeight={600} color="solid.900">{valueFormatter(entry.value, entry.payload)}</Box>
      </Text>
    </Box>
  )
}

/* ── SVG gradient + glow defs for horizontal bars ── */
export function BarGradientDefs({ ids }: { ids: { id: string; color: string }[] }) {
  return (
    <defs>
      {ids.map(({ id, color }) => (
        <linearGradient key={`g-${id}`} id={`grad-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="100%" stopColor={color} stopOpacity={1} />
        </linearGradient>
      ))}
      {ids.map(({ id, color }) => (
        <filter key={`f-${id}`} id={`glow-${id}`} x="-20%" y="-40%" width="140%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={color} floodOpacity="0.3" />
        </filter>
      ))}
    </defs>
  )
}

/* ── SVG gradient + glow defs for pie/donut slices ── */
export function PieGradientDefs({ ids }: { ids: { id: string; color: string }[] }) {
  return (
    <defs>
      {ids.map(({ id, color }) => (
        <linearGradient key={`g-${id}`} id={`pie-grad-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.6} />
          <stop offset="100%" stopColor={color} stopOpacity={1} />
        </linearGradient>
      ))}
      {ids.map(({ id, color }) => (
        <filter key={`f-${id}`} id={`pie-glow-${id}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1" stdDeviation="4" floodColor={color} floodOpacity="0.35" />
        </filter>
      ))}
    </defs>
  )
}

export const chartCardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] },
  },
}

interface ChartCardProps {
  title: string
  subtitle?: string
  action?: React.ReactNode
  children: React.ReactNode
  isLoading: boolean
  h?: string
}

export default function ChartCard({ title, subtitle, action, children, isLoading, h = '260px' }: ChartCardProps) {
  return (
    <MotionBox
      bg="white"
      borderRadius="16px"
      overflow="hidden"
      variants={chartCardVariants}
      whileHover={{ boxShadow: '0px 8px 16px -4px #00000012' }}
      transition={{ duration: 0.2 }}
    >
      <Flex px={{ base: 4, md: 5 }} py={4} align="center" justify="space-between">
        <Box>
          <Text textStyle="h-s" color="solid.900">{title}</Text>
          {subtitle && (
            <Text textStyle="b-xs" color="solid.400" mt="2px">{subtitle}</Text>
          )}
        </Box>
        {action}
      </Flex>
      <Box p={{ base: 3, md: 4 }} h={h}>
        {isLoading ? <Skeleton h="100%" borderRadius="8px" /> : children}
      </Box>
    </MotionBox>
  )
}
